/**
 * PasskeyEnrollFlow — in-profile passkey enrollment UX (WA-1b).
 *
 * Surfaces inside the "Two-factor auth" section of Admin Profile, opened
 * by "Add a passkey". Flow:
 *
 *   1. Optional nickname input (max 64 chars).
 *   2. User clicks "Register passkey" → POST enroll/begin →
 *      startRegistration({ optionsJSON: options }) browser ceremony
 *      ("Waiting for your device…").
 *   3. POST enroll/finish with { credential, nickname } → session issued.
 *      On success → ``onComplete()``, caller invalidates the credential list.
 *
 * Error handling per brief:
 *   - ``NotAllowedError`` from @simplewebauthn → inline, NOT a finish call.
 *   - enroll/finish 409 → "This passkey is already registered."
 *   - Any other error → generic inline message, re-begin available.
 *   - ``!window.PublicKeyCredential`` → browser-unsupported copy + nudge.
 *
 * No recovery codes for WebAuthn. No rename endpoint (WA-1a has none).
 */
import { useRef, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { startRegistration } from "@simplewebauthn/browser";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { Input } from "@/components/common/Input";
import { useAuthStore } from "@/stores/useAuthStore";
import { ApiError } from "@/lib/apiError";
import type {
  WebauthnBeginResponse,
  WebauthnFinishResponse,
} from "@/hooks/queries/admin/useAdminWebauthn";

type BeginMutation = UseMutationResult<WebauthnBeginResponse, ApiError, void>;
type FinishMutation = UseMutationResult<
  WebauthnFinishResponse,
  ApiError,
  { credential: Record<string, unknown>; nickname?: string | null }
>;

interface PasskeyEnrollFlowProps {
  beginMutation: BeginMutation;
  finishMutation: FinishMutation;
  /** Called after successful enroll + session issued. */
  onComplete: (accessToken: string) => void;
}

type Step =
  | "idle"
  | "ceremony"
  | "finishing"
  | "done";

type InlineError =
  | { kind: "none" }
  | { kind: "cancelled" }
  | { kind: "already_registered" }
  | { kind: "general"; text: string };

/** Returns true if the browser supports WebAuthn. */
function webAuthnSupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export function PasskeyEnrollFlow({
  beginMutation,
  finishMutation,
  onComplete,
}: PasskeyEnrollFlowProps): JSX.Element {
  const [nickname, setNickname] = useState("");
  const [step, setStep] = useState<Step>("idle");
  const [inlineError, setInlineError] = useState<InlineError>({ kind: "none" });

  // Keep the latest nickname in a ref so the async ceremony chain reads
  // the current value even if the user edits the field while waiting.
  const nicknameRef = useRef(nickname);
  nicknameRef.current = nickname;

  // ── Browser check ─────────────────────────────────────────────────────
  if (!webAuthnSupported()) {
    return (
      <div className="flex flex-col gap-3">
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          This browser doesn&rsquo;t support passkeys. Use your authenticator
          code instead.
        </div>
        <p className="text-xs text-text-secondary dark:text-brand-100/60">
          Keep your authenticator app (TOTP) or recovery codes active too.
          If a passkey is your only second factor and you switch to a browser
          that doesn&rsquo;t support passkeys, you&rsquo;ll be locked out and
          will need to contact support to regain access.
        </p>
      </div>
    );
  }

  const handleRegister = (): void => {
    setInlineError({ kind: "none" });
    setStep("ceremony");

    beginMutation.mutate(undefined, {
      onSuccess: async (data) => {
        // Pass the options blob verbatim to @simplewebauthn.
        let attResp: Awaited<ReturnType<typeof startRegistration>>;
        try {
          attResp = await startRegistration({
            optionsJSON: data.options as unknown as Parameters<typeof startRegistration>[0]["optionsJSON"],
          });
        } catch (err) {
          // User cancelled or hardware error — do NOT call finish (no lockout charge).
          const isUserCancel =
            err instanceof Error && err.name === "NotAllowedError";
          setStep("idle");
          if (isUserCancel) {
            setInlineError({ kind: "cancelled" });
          } else {
            const msg =
              err instanceof Error ? err.message : "Passkey error. Try again.";
            setInlineError({ kind: "general", text: msg });
          }
          return;
        }

        setStep("finishing");
        finishMutation.mutate(
          {
            credential: attResp as unknown as Record<string, unknown>,
            nickname: nicknameRef.current.trim() || null,
          },
          {
            onSuccess: (resp) => {
              // Stash the fresh access token in the auth store.
              useAuthStore.getState().loginSuccess(resp.access_token);
              setStep("done");
              onComplete(resp.access_token);
            },
            onError: (err) => {
              setStep("idle");
              if (err.status === 409) {
                setInlineError({ kind: "already_registered" });
              } else {
                setInlineError({
                  kind: "general",
                  text: "Couldn’t register passkey. Try again.",
                });
              }
            },
          },
        );
      },
      onError: () => {
        setStep("idle");
        setInlineError({
          kind: "general",
          text: "Couldn’t start passkey setup. Try again in a moment.",
        });
      },
    });
  };

  const isBusy = step === "ceremony" || step === "finishing";

  return (
    <div className="flex flex-col gap-4">
      {/* Inline error banners */}
      {inlineError.kind === "cancelled" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          Passkey prompt was cancelled &mdash; try again.
        </div>
      ) : null}
      {inlineError.kind === "already_registered" ? (
        <div
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400"
        >
          This passkey is already registered.
        </div>
      ) : null}
      {inlineError.kind === "general" ? (
        <div
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400"
        >
          {inlineError.text}
        </div>
      ) : null}

      <FormField
        label="Nickname (optional)"
        htmlFor="passkey-nickname"
      >
        <Input
          id="passkey-nickname"
          type="text"
          maxLength={64}
          placeholder="E.g. MacBook Touch ID or iPhone Face ID"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          disabled={isBusy}
          autoComplete="off"
        />
      </FormField>

      {step === "ceremony" ? (
        <p
          aria-live="polite"
          className="text-sm text-text-secondary dark:text-brand-100/70"
        >
          Waiting for your device&hellip;
        </p>
      ) : null}

      <Button
        type="button"
        className="w-full"
        loading={isBusy}
        disabled={isBusy}
        onClick={handleRegister}
      >
        Register passkey
      </Button>
    </div>
  );
}
